import Image from "next/image";
import Link from "next/link";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { useStaggerAnimation } from "@/hooks/useStaggerAnimation";
import SectionHeading from "@/components/home/SectionHeading";

/**
 * 제품 카테고리 — 피그마 S7 AllProducts(28983:88632).
 *
 * ⚠️ **시안에 있던 "$289부터" 가격 줄은 넣지 않았다.** 카테고리 최소가는 국가·통화·할인에 따라
 * 달라져서, 근거 없이 박아두면 실제 결제 금액과 어긋난 값을 노출하게 된다.
 * 어드민 최소가 연동이 되면 그때 붙인다 — 틀린 가격보다 없는 편이 낫다.
 *
 * ⚠️ 카드 이미지는 **Cool Gray/10 배경이 함께 구워진 342x342 익스포트**다(디자이너 제공).
 * 제품만 잘라 얹으면 제품별 여백(수트 300x183 / 나머지 256x256)이 달라 정렬이 어긋난다.
 *
 * 헤딩 카피의 정본은 **시안 국문 보드**다(기획 확정). 영문 보드 쪽은 다른 섹션에서 복사된
 * 플레이스홀더(`Compatible PS VR2 Games` / `Check out gameplay videos…`)라 쓰지 않고,
 * 국문을 옮겨 영문을 새로 썼다. 나머지 5개 locale 도 같은 의미로 맞췄다.
 *
 * ⚠️ 카드 제목 4종은 **모든 언어에서 영문 고정**이다(기획 확정). 시안 국문 보드는
 * `Accessories` 만 `액세서리` 로 되어 있지만, 나머지 3개와 통일해 영문으로 둔다.
 */
const BASE_PATH = "/images/home/main-renewal";

type Category = {
  key: string;
  href: string;
  image: string;
  alt: string;
  /** 영문 고정 — 제품/카테고리 이름이라 번역하지 않는다 */
  title: string;
  description: string;
};

const CATEGORIES: Category[] = [
  {
    key: "tactsuit",
    href: "/tactsuit",
    image: `${BASE_PATH}/cat-tactsuit.png`,
    alt: "TactSuit Pro · Air · Camo",
    title: "TactSuit",
    description:
      "Haptic suit lineup for feeling game sound and action through your body",
  },
  {
    key: "bundle",
    href: "/tactsuit/bundles",
    image: `${BASE_PATH}/cat-bundle.png`,
    alt: "Bundle",
    title: "Bundle",
    description: "Gaming haptic sets with TactSuit and accessories",
  },
  {
    key: "glove",
    href: "/shop/buy-tactsuit/tactglove-dk3",
    image: `${BASE_PATH}/cat-glove.png`,
    alt: "Glove",
    title: "Gloves",
    description: "Haptic gloves that extend feedback to your hands",
  },
  {
    key: "accessories",
    href: "/tactsuit/accessories",
    image: `${BASE_PATH}/cat-accessories.png`,
    alt: "Accessories",
    title: "Accessories",
    description: "Accessories that make your TactSuit setup easier",
  },
];

export default function ProductCategories({
  className,
}: {
  className?: string;
}) {
  const { t } = useTrans(["index"]);
  const { ref, className: delayClassName, delayFunc } = useStaggerAnimation({});

  return (
    <section className={cn("w-full px-4 laptop:px-10", className)}>
      <div className="mx-auto flex w-full max-w-screen-max flex-col items-center">
        <SectionHeading
          title={t("bHaptics Product Lineup")}
          description={t(
            "From TactSuit to bundles, gloves, and accessories — find the haptic gaming gear that fits you.",
          )}
        />

        <div
          ref={ref}
          className="mt-6 grid w-full grid-cols-2 gap-x-3 gap-y-5 laptop:mt-8 laptop:grid-cols-4 laptop:gap-6"
        >
          {CATEGORIES.map((category, index) => (
            <Link
              key={category.key}
              href={category.href}
              style={{ transitionDelay: delayFunc(index) }}
              className={cn(
                "group flex flex-col gap-3 laptop:gap-5",
                delayClassName,
              )}
            >
              <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-coolgray-10">
                <Image
                  src={category.image}
                  alt={t(category.alt)}
                  fill
                  sizes="(max-width: 1023px) 50vw, 342px"
                  className="object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col gap-1 laptop:gap-3">
                {/* 카테고리 이름은 제품명이라 번역하지 않는다 */}
                <h3 className="text-lg font-500 text-coolgray-90 laptop:text-2xl laptop:font-400">
                  {category.title}
                </h3>
                <p className="text-sm tracking-[0.4px] text-coolgray-90 laptop:text-base laptop:tracking-[0.25px]">
                  {t(category.description)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
